import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, LogOut, Plus, UserRound } from "lucide-react";

import { createWorkspace, logout, switchWorkspace, type Me } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

export function initialsOf(s: string): string {
  const parts = s.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const roleLabel = (r?: string) => (!r ? "" : r === "member" ? "viewer" : r);

export function UserMenu({ me, onSignedOut, collapsed }: { me: Me; onSignedOut: () => void; collapsed?: boolean }) {
  const nav = useNavigate();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [wsName, setWsName] = useState("");
  const [creating, setCreating] = useState(false);
  const workspaces = me.workspaces ?? [];
  const current = workspaces.find((w) => w.tenant_id === me.current_tenant_id);
  const display = me.name || me.email || me.user_id || "Account";

  async function signOut() {
    setSigningOut(true);
    await logout().catch(() => {});
    onSignedOut();
  }

  async function switchTo(tenantId: string) {
    if (tenantId === me.current_tenant_id || switching) return;
    setSwitching(true);
    try {
      await switchWorkspace(tenantId);
      // The whole data context changes with the workspace; a full navigation
      // re-fetches identity + every view cleanly.
      window.location.assign("/servers");
    } catch (e) {
      toast.error(humanizeError(e, "Couldn't switch workspace"));
      setSwitching(false);
    }
  }

  async function createWs(e: FormEvent) {
    e.preventDefault();
    const name = wsName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      // The server creates the workspace AND switches the session into it; a
      // full navigation lands the user in the fresh (empty) workspace.
      await createWorkspace(name);
      window.location.assign("/agents");
    } catch (err) {
      toast.error(humanizeError(err, "Couldn't create workspace"));
      setCreating(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            title={display}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-accent",
              collapsed && "lg:justify-center",
            )}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {initialsOf(display)}
            </span>
            <span className={cn("min-w-0 flex-1", collapsed && "lg:hidden")}>
              <span className="block truncate text-sm font-medium">{display}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {current ? `${current.name ?? current.tenant_id} · ${roleLabel(current.role)}` : me.email}
              </span>
            </span>
            <ChevronsUpDown className={cn("h-4 w-4 shrink-0 text-muted-foreground", collapsed && "lg:hidden")} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[15rem]">
          <div className="px-2 py-1.5">
            <div className="truncate text-sm font-medium">{me.name || display}</div>
            {me.email && <div className="truncate text-xs text-muted-foreground">{me.email}</div>}
          </div>
          {workspaces.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Workspaces</div>
              {workspaces.map((w) => {
                const isCurrent = w.tenant_id === me.current_tenant_id;
                return (
                  <DropdownMenuItem
                    key={w.tenant_id}
                    onSelect={(e) => {
                      e.preventDefault();
                      void switchTo(w.tenant_id);
                    }}
                    disabled={switching}
                  >
                    <Check className={cn("h-4 w-4", isCurrent ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{w.name ?? w.tenant_id}</span>
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              // Defer opening so the dropdown finishes closing + releases focus
              // before the dialog mounts; otherwise Radix's focus-return races
              // the dialog's autoFocus and the input may not get focus.
              e.preventDefault();
              setTimeout(() => setCreateOpen(true), 0);
            }}
          >
            <Plus /> New workspace
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => nav("/profile")}>
            <UserRound /> Profile &amp; security
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={signOut} className="text-destructive focus:text-destructive">
            {signingOut ? <Spinner className="h-4 w-4" /> : <LogOut />} Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={(o) => !creating && setCreateOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>
              Create a separate workspace you own. You'll switch into it right away.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createWs} className="space-y-4">
            <Input
              autoFocus
              placeholder="Workspace name"
              value={wsName}
              maxLength={80}
              onChange={(e) => setWsName(e.target.value)}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button type="submit" disabled={!wsName.trim() || creating}>
                {creating ? <Spinner className="h-4 w-4" /> : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
